import { Container, Graphics, HTMLText } from "pixi.js";
import { HEIGHT, TEXT_FONT, theme } from "../constants";
import GameNode from "./GameNode";
import { inputManager } from "../inputs";
import { getScores, setScores, type Score } from "../storage";
import { playSound } from "../audio";
import { container } from "../util";
import { pulse } from "../animations";
import { setI18nKey } from "../i18n";
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

type State = "enter_name" | "high_scores";

// List of player scores
export default class ScoreScreen extends GameNode {
  score: number;
  letters: HTMLText[];
  #activeIndex = 0;
  nameEnter = new Container();
  arrows = new Container();
  state: State = "enter_name";
  playerIndex: string;
  // Called when the player finishes typing their name
  onInputFinish?: () => void;
  onFinish?: () => void;

  constructor(score: number, playerIndex: string) {
    super();
    this.score = score;
    this.playerIndex = playerIndex;

    const containerSize = HEIGHT * 0.75;
    this.view.addChild(
      container(
        new Graphics().roundRect(
          -containerSize / 2,
          -containerSize / 2,
          containerSize,
          containerSize
        )
      )
    );

    this.letters = [];
    for (let i = 0; i < 3; i++) {
      this.letters.push(
        new HTMLText({
          text: "A",
          style: {
            fill: "grey",
            fontFamily: TEXT_FONT,
            fontWeight: "bold",
            fontSize: 256,
          },
        })
      );
    }
    const title = new HTMLText({
      style: {
        fill: theme.colors.primary,
        fontFamily: TEXT_FONT,
        fontWeight: "bold",
        fontSize: 48,
      },
    });
    setI18nKey(title, "game.enter_name");
    title.anchor = { x: 0.5, y: 0.5 };
    title.position.y = -300;
    this.nameEnter.addChild(title);

    this.letters[0].position = { x: -200, y: 0 };
    this.letters[1].position = { x: 0, y: 0 };
    this.letters[2].position = { x: 200, y: 0 };
    for (let letter of this.letters) {
      letter.anchor = { x: 0.5, y: 0.5 };
      this.nameEnter.addChild(letter);
    }

    const arrowRadius = 30;
    const arrowBase = 125;
    this.arrows.addChild(
      new Graphics()
        .poly([
          -arrowRadius,
          arrowBase,
          0,
          arrowBase + arrowRadius,
          arrowRadius,
          arrowBase,
        ])
        .fill(theme.colors.primary),
      new Graphics()
        .poly([
          -arrowRadius,
          -arrowBase,
          0,
          -(arrowBase + arrowRadius),
          arrowRadius,
          -arrowBase,
        ])
        .fill(theme.colors.primary)
    );
    this.nameEnter.addChild(this.arrows);

    this.letters[this.#activeIndex].style.fill = theme.colors.primary;
    this.arrows.position.x = this.letters[this.#activeIndex].position.x;
    this.view.addChild(this.nameEnter);
  }

  showHighScores() {
    this.onInputFinish?.();
    this.state = "high_scores";
    this.view.removeChild(this.nameEnter);
    const scores = getScores();
    let index = scores.findIndex((entry) => entry.score < this.score);
    if (index === -1) {
      index = scores.length;
    }
    const name = this.letters.map((letter) => letter.text).join("");
    const newEntry = {
      name,
      score: this.score,
    };
    scores.splice(index, 0, newEntry);
    setScores(scores);
    const highScoresLabel = new HTMLText({
      style: {
        fill: theme.colors.primary,
        fontFamily: TEXT_FONT,
        fontSize: 72,
        fontWeight: "bold",
      },
    });
    setI18nKey(highScoresLabel, "menu.high_scores");
    highScoresLabel.position.y = -300;
    highScoresLabel.anchor = { x: 0.5, y: 0.5 };
    this.view.addChild(highScoresLabel);
    for (let [i, entry] of scores.slice(0, 8).entries()) {
      this.showEntry(i, entry, -200 + i * 60, i === index);
    }
    this.showEntry(index, newEntry, -200 + 9 * 60, true);
  }

  showEntry(index: number, entry: Score, position: number, highlight = false) {
    const fill = highlight ? theme.colors.primary : theme.colors.muted;
    const nameText = new HTMLText({
      text: `${(index + 1 + "").padStart(3)} ${entry.name}`,
      style: {
        fontFamily: TEXT_FONT,
        fill,
        fontSize: 48,
        fontWeight: "bold",
      },
    });
    nameText.position = { x: -300, y: position };
    nameText.anchor = { x: 0, y: 0.5 };
    const scoreText = new HTMLText({
      text: entry.score,
      style: {
        fontFamily: TEXT_FONT,
        fill,
        fontSize: 48,
        fontWeight: "bold",
      },
    });
    scoreText.position = { x: 250, y: position };
    scoreText.anchor = { x: 1, y: 0.5 };
    this.view.addChild(scoreText);
    this.view.addChild(nameText);
  }

  handleKeyDown = (input: string) => {
    if (this.state === "enter_name") {
      switch (input) {
        // up/down: change letter
        case `${this.playerIndex}.up`: {
          const currentLetter = this.letters[this.#activeIndex];
          const letterIndex = LETTERS.indexOf(currentLetter.text);
          currentLetter.text = LETTERS[(letterIndex || LETTERS.length) - 1];
          pulse(currentLetter, 1.1);
          playSound("move");
          break;
        }
        case `${this.playerIndex}.down`: {
          const currentLetter = this.letters[this.#activeIndex];
          const letterIndex = LETTERS.indexOf(currentLetter.text);
          currentLetter.text = LETTERS[(letterIndex + 1) % LETTERS.length];
          pulse(currentLetter, 1.1);
          playSound("move");
          break;
        }
        // left/right: change active index
        case `${this.playerIndex}.left`: {
          this.activeIndex = (this.activeIndex || this.letters.length) - 1;
          playSound("move");
          break;
        }
        case `${this.playerIndex}.right`: {
          this.activeIndex = (this.activeIndex + 1) % this.letters.length;
          playSound("move");
          break;
        }
        case `${this.playerIndex}.flip`: {
          playSound("clear");
          this.showHighScores();
          break;
        }
      }
    } else {
      // Press any key to go back to start
      if (input.startsWith(this.playerIndex)) {
        this.onFinish?.();
      }
    }
  };

  get activeIndex() {
    return this.#activeIndex;
  }

  set activeIndex(value: number) {
    this.letters[this.#activeIndex].style.fill = theme.colors.muted;
    this.#activeIndex = value;
    const active = this.letters[this.#activeIndex];
    active.style.fill = theme.colors.primary;
    this.arrows.position.x = active.position.x;
    pulse(active, 1.1);
  }

  start() {
    playSound("ending");
    inputManager.addKeydownListener(this.handleKeyDown);
  }

  destroy() {
    inputManager.removeKeydownListener(this.handleKeyDown);
  }
}
